import React, { useState, useRef, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, FormGroup, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import SimpleReactValidator from 'simple-react-validator';
import styles from './CancelContractModal.module.css';
import moment from 'moment';
import CheckboxInput from '../shared/CheckboxInput/CheckboxInput';
import { ContractContext } from '../contexts/ContractContext';
import { DropDownInput } from '../inputs/DropDownInput';
import FooterButtonsRow from '../shared/FooterButtonsRow/FooterButtonsRow';
import ExpandableTextarea from '../shared/ExpandableTextarea/ExpandableTextarea';
import getClientContent, {
    CancellationsModalAffirmation,
    CancelModalParagraph
} from '../_BrandingProvider/brandContent.jsx';
import { getContractsByPartyId } from '../../helpers';

const CancelContractModal = ({ isOpen, handleSubmit, centered, toggle }) => {
    const history = useHistory();
    const context = useContext(ContractContext);
    const clientId = context?.data?.clientId || context?.data?.contract?.clientId || 'aig';
    const contractId = context.data?.contract?.contractId || context.data?.contracts[0]?.contractId || null;
    const partyName = context.data?.party?.partyName || '';
    const partyId = context.data?.party?.partyId;
    const coveragePlanType = context.data?.coveragePlanType ?? 'HomeWarranty';
    const [cancelOptions, setCancelOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState();
    const [description, setDescription] = useState('');
    const [affirmed, setAffirmed] = useState(false);
    const [validationFired, setValidationFired] = useState(false);
    const [, forceUpdate] = useState();
    const cancellationDate = moment(new Date()).format('MM/DD/YYYY');
    const [showSpinner, setShowSpinner] = useState(false);

    const validator = useRef(
        new SimpleReactValidator({
            validators: {
                affirmation: {
                    rule: (val, params, validator) => {
                        return val;
                    },
                    required: true
                }
            },
            element: (message) => <div className='errorMsg'>{message.charAt(0).toUpperCase() + message.slice(1)}</div>,
            messages: {
                required: ':attribute is required.',
                affirmation: 'Please affirm to continue.'
            }
        })
    );

    const getCancellationReasons = () => {
        fetch(`api/configuration/GetConfigByName/CancellationReason/${clientId}`)
            .then((res) => res.json())
            .then((data) => {
                const reasons = data?.reasons?.map((item) => {
                    return {
                        data: item.id,
                        display: item.reason
                    };
                });
                setCancelOptions(reasons);
            });
    };

    useEffect(() => {
        getCancellationReasons();
    }, [clientId]);

    const handleCancelReason = (opt) => {
        setSelectedOption(opt);
    };

    const handleDescription = (e) => {
        setDescription(e.target.value);
    };

    const handleCheckBoxChange = () => {
        setAffirmed(!affirmed);
    };

    const handleTermsLink = (e) => {
        e.preventDefault();
        window.open('/coveragedocs', '_blank');
    };

    const handleSubmitAction = () => {
        setValidationFired(true);

        if (validator.current.allValid()) {
            setShowSpinner(true);
            setValidationFired(false);

            const requestObj = {
                ContractId: contractId,
                FullRefundFlag: 0,
                Executedby: partyName || 'CustomerPortal',
                DetailedReason: description,
                CancellationReasonId: selectedOption,
                CancelRequestDate: cancellationDate
            };
            fetch(`api/contract/CancelContract/${clientId}`, {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestObj)
            }).then((res) => {
                if (res.ok && res.status === 200) {
                    res.json().then(async (data) => {
                        if (data.responseCode === '00') {
                            const contracts = await getContractsByPartyId(partyId, clientId).finally(() =>
                                handleSubmit()
                            );
                            context.updateContract(contracts[0]);
                            context.updateContracts(contracts);
                            setShowSpinner(false);
                        } else {
                            setShowSpinner(false);
                            history.push('/system-error');
                        }
                    });
                } else {
                    setShowSpinner(false);
                    history.push('/system-error');
                }
            });
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            centered={centered}
            toggle={toggle}
            className='modal-md'
            aria-modal='true'
            role='dialog'
            aria-labelledBy='open'
        >
            <ModalBody className={`${styles.modalContainer} text-left`}>
                <h5>Are you sure you want to cancel this plan?</h5>
                <CancelModalParagraph brandId={clientId} />

                <p>If you would still like to cancel, it would be helpful to know why.</p>
                <Row>
                    <Col
                        md='6'
                        className={styles.reasonDropdown}
                    >
                        <FormGroup className={validationFired && !selectedOption ? 'form-error' : ''}>
                            <Label
                                for='cancellationReason'
                                className='form-label'
                            >
                                Cancellation Reason
                            </Label>
                            <span
                                aria-hidden='true'
                                className='required-indicator'
                            >
                                *
                            </span>
                            <DropDownInput
                                name='cancellationReason'
                                options={cancelOptions}
                                className='form-control'
                                onChangedValue={handleCancelReason}
                                placeholder='Please Select...'
                                defaultValue={selectedOption}
                                validator={validator.current.message('cancellationReason', selectedOption, 'required')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md='11'>
                        <ExpandableTextarea
                            hasError={
                                validationFired && validator.current.message('description', description, 'required')
                            }
                            label='Please provide a detailed explanation.'
                            minRows={3}
                            onChange={handleDescription}
                            value={description}
                            placeholder='Enter description'
                            maxLength={500}
                            validator={validator.current.message('description', description, 'required', {
                                messages: { required: 'Enter a description to continue.' }
                            })}
                        />
                    </Col>
                    <Col md='12'>
                        <CheckboxInput
                            id='affirmation'
                            customClass='mb-1'
                            checked={affirmed}
                            handleCheck={handleCheckBoxChange}
                            clientId={clientId}
                        >
                            <CancellationsModalAffirmation brandId={clientId} />
                        </CheckboxInput>
                        <p>{validationFired && validator.current.message('affirmation', affirmed, 'affirmation')}</p>
                        <div>
                            Cancellation requests will be processed within 1 to 2 business days and it can take up to 30
                            days for any applicable refunds to be sent.
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <FooterButtonsRow
                    isModal
                    buttons={{
                        link: {
                            text: 'Cancel',
                            onClick: toggle,
                            ariaLabel: 'Click cancel to close modal and return to my plans tab'
                        },
                        primary: {
                            hasSpinner: true,
                            isSpinning: showSpinner,
                            disabled: showSpinner,
                            text: 'Submit',
                            type: 'primary',
                            ariaLabel: 'Submit',
                            onClick: handleSubmitAction
                        }
                    }}
                />
            </ModalFooter>
        </Modal>
    );
};

export default CancelContractModal;
